import React from 'react'
import { googleCalendarUrl, localInstant } from '../calendarLink.js';
const { useState, useEffect, useCallback, useMemo, useRef } = React;

import {
  AirtableAPI, TABLE_IDS, CURRENT_USER, COMPANY_PROFILE,
  callOpenAI, navSetUrl, SOURCE_OPTIONS, OPP_STAGES, WON_STAGES, CLOSED_STAGES,
  BENCH_REPLY_HIGH, BENCH_REPLY_LOW, BENCH_MEETING_HIGH, BENCH_MEETING_LOW,
  CHANNEL_BENCHMARKS, MESSAGE_PROMPTS, MESSAGE_PROMPT_DEFAULTS, saveMessagePrompts,
  resolvePromptTemplate, saveCompanyProfile, channelIcon, logoutUser,
  CLIENT_CONFIG, AUTH_TOKEN, loadBranding, BRANDING_LS_KEY,
} from '../globals.js';
import {
  F, linkedIds, resolveLinked, InfoTip, REPLY_STATUSES, computeEnrichment,
  DIAGNOSIS_CONFIG, findDuplicateStakeholder, confirmDuplicateStakeholder,
  deriveStakeholderStatus, updateStakeholderStatus, STAKEHOLDER_STATUS_PRIORITY,
  STAKEHOLDER_STATUS_PROTECTED, activateAccountIfNeeded,
  formatCurrency, formatDate, strSimilarity, FileNotesRenderer,
} from '../utils.jsx';
import { confirmNoDuplicate } from '../teamClient.js';
import StakeholderHistoryModal from './StakeholderHistoryModal.jsx';
import { sendContactMessage } from '../contactMessage.js';
import { setReturn } from './operations/ReturnToList.jsx';
import { es } from '../labelsEs.js';
import EditModal from './EditModal.jsx';

// A light check before saving: something@something.something, no spaces.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


function ContactsSection({ data, api, onLogActivity, onAddRecord, onUpdateRecord, onDeleteRecord, goToAccount }) {
  const { accounts, stakeholders, outreach, campaigns = [], sources = [] } = data;
  const [searchName, setSearchName] = useState('');
  const [searchAccount, setSearchAccount] = useState('');
  const [selectedInfluence, setSelectedInfluence] = useState('');
  const [historyStakeholder, setHistoryStakeholder] = useState(null);

  // Listen for global search navigation to open a specific contact
  useEffect(() => {
    const handler = (e) => {
      const { id, record } = e.detail || {};
      const found = record || stakeholders.find(s => s.id === id);
      if (found) setHistoryStakeholder(found);
    };
    window.addEventListener('oike:openContact', handler);
    return () => window.removeEventListener('oike:openContact', handler);
  }, [stakeholders]);
  const [showNewContact, setShowNewContact] = useState(false);
  const [ctxNewName, setCtxNewName] = useState('');
  const [ctxNewLast, setCtxNewLast] = useState('');
  const [ctxNewRole, setCtxNewRole] = useState('');
  const [ctxNewEmail, setCtxNewEmail] = useState('');
  const [ctxNewPhone, setCtxNewPhone] = useState('');
  const [ctxNewLinkedin, setCtxNewLinkedin] = useState('');
  const [ctxNewInfluence, setCtxNewInfluence] = useState('');
  const [ctxNewAccountId, setCtxNewAccountId] = useState('');
  const [ctxNewSource, setCtxNewSource] = useState('');
  const [ctxNewCampaign, setCtxNewCampaign] = useState('');
  const [ctxCreating, setCtxCreating] = useState(false);
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [ctxNewAccountName, setCtxNewAccountName] = useState('');
  const [ctxNewAccountWebsite, setCtxNewAccountWebsite] = useState('');
  const [ctxCreatingAccount, setCtxCreatingAccount] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [meetingModal, setMeetingModal] = useState(null); // { stakeholder }
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterDealRole, setFilterDealRole] = useState('');
  const [bulkPainProgress, setBulkPainProgress] = useState(null); // {done, total} or null
  const STAKEHOLDER_STATUS_OPTIONS = ['Not Contacted', 'Contacted', 'Replied', 'Meeting Booked', 'Not Interested', 'DNC', 'Bounced', 'Left Company', 'Nurture'];
  const [showContactImport, setShowContactImport] = useState(false);
  const [contactCsvRows, setContactCsvRows] = useState([]);
  const [contactImporting, setContactImporting] = useState(false);
  const [updateDuplicates, setUpdateDuplicates] = useState(false);
  const [csvCampaignId, setCsvCampaignId] = useState(''); // campaign applied to all imported contacts
  const [contactImportResult, setContactImportResult] = useState(null);

  const isInbound = (src) => src && src.startsWith('Inbound');

  const createInlineAccount = async () => {
    if (!ctxNewAccountName.trim()) return;
    setCtxCreatingAccount(true);
    // Need real ID to link contact — must await, but skip full reload
    try {
      const a = api || new AirtableAPI();
      const fields = { 'Account Name': ctxNewAccountName.trim() };
      if (ctxNewAccountWebsite.trim()) fields['Website'] = ctxNewAccountWebsite.trim();
      // The team of the central base: the server assigns it (with its history); never a name stamp
      if (CURRENT_USER?.role === 'bdr' && !CURRENT_USER?.teamMode) fields['BDR Owner'] = CURRENT_USER?.name || '';
      if (!(await confirmNoDuplicate('account', fields))) { setCtxCreatingAccount(false); return; }
      if (CURRENT_USER?.role === 'cp') fields['CP Assigned'] = CURRENT_USER?.name || '';
      const record = await a.createRecord(TABLE_IDS.accounts, fields);
      if (record?.id) {
        setCtxNewAccountId(record.id);
        // Add to local state with real ID — no full reload needed
        if (onAddRecord) onAddRecord('accounts', fields, record.id);
      }
      setCtxNewAccountName(''); setCtxNewAccountWebsite('');
      setShowNewAccount(false);
    } catch (e) { console.error(e); window.__oikeToast(e?.code ? e.message : 'No se pudo crear la empresa', 'error'); }
    setCtxCreatingAccount(false);
  };

  const createContact = async () => {
    if (!ctxNewName.trim()) return;
    if (ctxNewEmail.trim() && !EMAIL_RE.test(ctxNewEmail.trim())) return;
    const fields = { 'Name': ctxNewName.trim() };
    if (ctxNewAccountId) fields['Account'] = [ctxNewAccountId];
    if (ctxNewLast.trim()) fields['Last name'] = ctxNewLast.trim();
    if (ctxNewRole.trim()) fields['Role'] = ctxNewRole.trim();
    if (ctxNewEmail.trim()) fields['Email'] = ctxNewEmail.trim();
    if (ctxNewPhone.trim()) fields['Phone number'] = ctxNewPhone.trim();
    if (ctxNewLinkedin.trim()) fields['LinkedIn'] = ctxNewLinkedin.trim();
    if (ctxNewInfluence) fields['Level of Influence'] = ctxNewInfluence;
    if (ctxNewSource) fields['Source'] = ctxNewSource;
    if (ctxNewCampaign) fields['Campaign'] = [ctxNewCampaign]; // linked record by ID
    if (CURRENT_USER?.role === 'bdr') fields['BDR Owner'] = CURRENT_USER?.name || '';
    if (CURRENT_USER?.role === 'cp') fields['CP Assigned'] = CURRENT_USER?.name || '';
    // Duplicate check (the team of the central base asks the server: it cannot see records outside its reach)
    if (!(await confirmNoDuplicate('contact', fields))) return;
    const dup = findDuplicateStakeholder(fields, stakeholders);
    if (dup && !confirmDuplicateStakeholder(dup)) return;
    // Optimistic: show instantly
    if (onAddRecord) onAddRecord('stakeholders', fields);
    // Close form immediately
    setCtxNewName(''); setCtxNewLast(''); setCtxNewRole(''); setCtxNewEmail('');
    setCtxNewPhone(''); setCtxNewLinkedin(''); setCtxNewInfluence(''); setCtxNewAccountId('');
    setCtxNewSource(''); setCtxNewCampaign('');
    setShowNewContact(false);
    // API in background
    const a = api || new AirtableAPI();
    a.createRecord(TABLE_IDS.stakeholders, fields)
      .then(() => { if (onLogActivity) onLogActivity(); })
      .catch(e => { console.error(e); window.__oikeToast('No se pudo crear el contacto', 'error'); if (onLogActivity) onLogActivity(); });
  };

  // ─── CSV IMPORT ───
  const handleContactCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (result) => {
        const existingEmails = new Set(stakeholders.map(s => (F(s, 'Email') || '').toLowerCase()).filter(Boolean));
        const existingFullNames = stakeholders.map(s => ((F(s, 'Name') || '') + ' ' + (F(s, 'Last name') || '')).trim());
        const rows = result.data.map(row => {
          const norm = {};
          Object.keys(row).forEach(k => {
            const kl = k.toLowerCase().trim();
            if (kl === 'first name' || kl === 'firstname' || kl === 'name' || kl === 'nombre') norm.firstName = row[k]?.trim();
            if (kl === 'last name' || kl === 'lastname' || kl === 'apellido') norm.lastName = row[k]?.trim();
            if (kl === 'email' || kl === 'correo') norm.email = row[k]?.trim();
            if (kl === 'phone' || kl === 'telefono' || kl === 'phone number' || kl === 'teléfono') norm.phone = row[k]?.trim();
            if (kl === 'role' || kl === 'title' || kl === 'cargo' || kl === 'job title') norm.role = row[k]?.trim();
            if (kl === 'linkedin' || kl === 'linkedin url') norm.linkedin = row[k]?.trim();
            if (kl === 'level of influence' || kl === 'influence' || kl === 'influencia') norm.influence = row[k]?.trim();
            if (kl === 'account' || kl === 'company' || kl === 'empresa') norm.accountName = row[k]?.trim();
            if (kl === 'source' || kl === 'fuente') norm.source = row[k]?.trim();
            if (kl === 'campaign' || kl === 'campaña') norm.campaign = row[k]?.trim();
            if (kl === 'country' || kl === 'pais' || kl === 'país') norm.country = row[k]?.trim();
            if (kl === 'website' || kl === 'web' || kl === 'url' || kl === 'account website' || kl === 'company website') norm.website = row[k]?.trim();
            if (kl === 'industry' || kl === 'industria' || kl === 'sector') norm.industry = row[k]?.trim();
          });
          if (!norm.firstName) return null;
          // Auto-inherit country from matched account if not set
          // Domain-based account matching (fallback when no Account column or name doesn't match)
          const GENERIC_DOMAINS_CSV = new Set(['gmail.com','yahoo.com','hotmail.com','outlook.com','icloud.com','live.com','protonmail.com','me.com','aol.com','mail.com']);
          const extractDomain = (url) => {
            if (!url) return null;
            try { return new URL(url.startsWith('http') ? url : 'https://' + url).hostname.replace(/^www\./, '').toLowerCase(); } catch { return null; }
          };
          const emailDomain = (email) => {
            const p = (email || '').split('@'); return p.length === 2 && !GENERIC_DOMAINS_CSV.has(p[1].toLowerCase()) ? p[1].toLowerCase() : null;
          };
          const findAccByDomain = (domain) => {
            if (!domain) return null;
            return accounts.find(ac => { const w = (F(ac, 'Website') || '').toLowerCase(); return w && w.includes(domain); }) || null;
          };
          if (!norm.resolvedAccountId) {
            const wDomain = extractDomain(norm.website);
            const eDomain = emailDomain(norm.email);
            const byWeb = findAccByDomain(wDomain);
            const byEmail = !byWeb && findAccByDomain(eDomain);
            const domainMatch = byWeb || byEmail;
            if (domainMatch) {
              norm.resolvedAccountId = domainMatch.id;
              if (!norm.accountName) norm.accountName = F(domainMatch, 'Account Name') || '';
            }
          }
          if (!norm.country && norm.accountName) {
            const matchedAcc = accounts.find(ac => (F(ac, 'Account Name') || '').toLowerCase() === (norm.accountName || '').toLowerCase())
              || (norm.resolvedAccountId ? accounts.find(ac => ac.id === norm.resolvedAccountId) : null);
            if (matchedAcc) norm.country = F(matchedAcc, 'Country') || '';
          }
          const fullName = (norm.firstName + ' ' + (norm.lastName || '')).trim();
          // Exact match
          const emailExact = norm.email && existingEmails.has(norm.email.toLowerCase());
          const nameExact = existingFullNames.some(n => n.toLowerCase() === fullName.toLowerCase());
          if (emailExact) return { ...norm, isDuplicate: true, duplicateReason: 'Ya existe ese email', isFuzzy: false, selected: false };
          if (nameExact) return { ...norm, isDuplicate: true, duplicateReason: 'Ya existe ese nombre', isFuzzy: false, selected: false };
          // Fuzzy name match
          const fuzzyMatch = existingFullNames.find(n => strSimilarity(n, fullName) >= 0.78);
          if (fuzzyMatch) return { ...norm, isDuplicate: false, isFuzzy: true, fuzzyReason: `Similar to "${fuzzyMatch}"`, selected: true };
          return { ...norm, isDuplicate: false, isFuzzy: false, selected: true };
        }).filter(Boolean);
        setContactCsvRows(rows);
        setContactImportResult(null);
      }
    });
  };

  const importContacts = async () => {
    const toCreate = contactCsvRows.filter(r => r.selected && !r.isDuplicate);
    const toUpdate = updateDuplicates ? contactCsvRows.filter(r => r.isDuplicate) : [];
    if (toCreate.length === 0 && toUpdate.length === 0) return;
    setContactImporting(true);
    let created = 0, failed = 0;
    let updated = 0, updateSkipped = 0;
    let websitesAdded = 0;
    let industriesAdded = 0;
    let accountsCreated = 0;
    const createdStakeholderIds = []; // track for campaign assignment
    const a = api || new AirtableAPI();
    const toImport = toCreate; // alias for loop below (creation flow stays the same)
    // Track account-level fields we already wrote this session to avoid redundant writes
    const accountWebsiteUpdated = new Set();
    const accountIndustryUpdated = new Set();
    // Build lookup map: lowerAccountName → account record (existing + newly-created this session)
    const accountMap = new Map();
    accounts.forEach(ac => {
      const nm = (F(ac, 'Account Name') || '').toLowerCase().trim();
      if (nm) accountMap.set(nm, ac);
    });

    for (const row of toImport) {
      try {
        // Resolve or create account by name or pre-resolved domain match
        let matchedAcc = null;
        if (row.resolvedAccountId) {
          matchedAcc = accounts.find(ac => ac.id === row.resolvedAccountId) || accountMap.get((row.accountName || '').toLowerCase().trim()) || null;
        }
        if (!matchedAcc && row.accountName) {
          const key = row.accountName.toLowerCase().trim();
          matchedAcc = accountMap.get(key);
          if (!matchedAcc) {
            // Account doesn't exist — create it on-the-fly
            try {
              const newAccFields = { 'Account Name': row.accountName.trim() };
              if (row.website) newAccFields['Website'] = row.website;
              if (row.industry) newAccFields['Industry'] = row.industry;
              if (row.country) newAccFields['Country'] = row.country;
              if (CURRENT_USER?.role === 'bdr' && !CURRENT_USER?.teamMode && CURRENT_USER?.name) newAccFields['BDR Owner'] = [{ id: CURRENT_USER.id }];
              const newAcc = await a.createRecord(TABLE_IDS.accounts, newAccFields);
              if (newAcc?.id) {
                matchedAcc = { id: newAcc.id, fields: newAccFields };
                accountMap.set(key, matchedAcc);
                accountsCreated++;
                // Mark website/industry as already set (came from creation, not update)
                if (row.website) accountWebsiteUpdated.add(newAcc.id);
                if (row.industry) accountIndustryUpdated.add(newAcc.id);
              }
            } catch (accErr) {
              console.warn('[Import] account create failed for', row.accountName, accErr);
            }
          }
        }
        const fields = { 'Name': row.firstName };
        if (row.lastName) fields['Last name'] = row.lastName;
        if (row.email) fields['Email'] = row.email;
        if (row.phone) fields['Phone number'] = row.phone;
        if (row.role) fields['Role'] = row.role;
        if (row.linkedin) fields['LinkedIn'] = row.linkedin;
        if (row.influence) fields['Level of Influence'] = row.influence;
        if (row.source) fields['Source'] = row.source;
        // Resolve campaign: manual selector takes priority, then CSV column by name match
        // NOTE: do NOT set stakeholder's 'Campaign' field — that's the reverse link of
        // 'Stakeholders Reached' and would mark them as reached. Campaign assignment is
        // handled by updating 'Assigned Stakeholders' on the campaign record (done after loop).
        const resolvedCampaignId = csvCampaignId
          || (row.campaign ? (campaigns.find(c => (F(c,'Name')||'').toLowerCase() === row.campaign.toLowerCase())?.id || '') : '');
        const resolvedCountry = row.country || (matchedAcc ? F(matchedAcc, 'Country') : '') || '';
        if (resolvedCountry) fields['Country'] = resolvedCountry;
        if (matchedAcc) fields['Account'] = [matchedAcc.id];
        if (CURRENT_USER?.role === 'bdr') fields['BDR Owner'] = CURRENT_USER?.name || '';
        const newStk = await a.createRecord(TABLE_IDS.stakeholders, fields);
        if (newStk?.id && resolvedCampaignId) createdStakeholderIds.push(newStk.id);
        created++;
        // Fill account-level fields from CSV if account matched and fields are empty
        if (matchedAcc) {
          const accountUpdate = {};
          if (row.website && !accountWebsiteUpdated.has(matchedAcc.id) && !F(matchedAcc, 'Website')) {
            accountUpdate['Website'] = row.website;
            accountWebsiteUpdated.add(matchedAcc.id);
            websitesAdded++;
          }
          if (row.industry && !accountIndustryUpdated.has(matchedAcc.id) && !F(matchedAcc, 'Industry')) {
            accountUpdate['Industry'] = row.industry;
            accountIndustryUpdated.add(matchedAcc.id);
            industriesAdded++;
          }
          if (Object.keys(accountUpdate).length > 0) {
            try {
              await a.updateRecord(TABLE_IDS.accounts, matchedAcc.id, accountUpdate);
            } catch (wErr) {
              console.warn('[Import] account update failed for', matchedAcc.id, wErr);
              // Rollback counters if update failed
              if (accountUpdate['Website']) { accountWebsiteUpdated.delete(matchedAcc.id); websitesAdded--; }
              if (accountUpdate['Industry']) { accountIndustryUpdated.delete(matchedAcc.id); industriesAdded--; }
            }
          }
        }
        await new Promise(r => setTimeout(r, 250));
      } catch (e) { failed++; console.error(e); }
    }
    // ── Helper: find or create an account (used by update pass) ──
    const resolveOrCreateAccount = async (row) => {
      // Pre-resolved via domain match in parser
      if (row.resolvedAccountId) {
        const found = accounts.find(ac => ac.id === row.resolvedAccountId);
        if (found) return found;
      }
      if (!row.accountName) return null;
      const key = row.accountName.toLowerCase().trim();
      let matched = accountMap.get(key);
      if (matched) return matched;
      try {
        const newAccFields = { 'Account Name': row.accountName.trim() };
        if (row.website) newAccFields['Website'] = row.website;
        if (row.industry) newAccFields['Industry'] = row.industry;
        if (row.country) newAccFields['Country'] = row.country;
        const newAcc = await a.createRecord(TABLE_IDS.accounts, newAccFields);
        if (newAcc?.id) {
          matched = { id: newAcc.id, fields: newAccFields };
          accountMap.set(key, matched);
          accountsCreated++;
          if (row.website) accountWebsiteUpdated.add(newAcc.id);
          if (row.industry) accountIndustryUpdated.add(newAcc.id);
        }
      } catch (e) { console.warn('[resolveOrCreateAccount] failed:', e); }
      return matched;
    };

    // ── Update pass for duplicates (if enabled) ──
    for (const row of toUpdate) {
      try {
        // Find the existing stakeholder by email first, then by full name
        let existing = null;
        if (row.email) {
          existing = stakeholders.find(s => (F(s, 'Email') || '').toLowerCase() === row.email.toLowerCase());
        }
        if (!existing) {
          const fullName = ((row.firstName || '') + ' ' + (row.lastName || '')).trim().toLowerCase();
          existing = stakeholders.find(s => (((F(s, 'Name') || '') + ' ' + (F(s, 'Last name') || '')).trim().toLowerCase()) === fullName);
        }
        if (!existing) { updateSkipped++; continue; }

        // Build fields to update — ONLY fill empty fields (never overwrite existing data)
        const updates = {};
        if (row.email && !F(existing, 'Email')) updates['Email'] = row.email;
        if (row.phone && !F(existing, 'Phone number')) updates['Phone number'] = row.phone;
        if (row.role && !F(existing, 'Role')) updates['Role'] = row.role;
        if (row.linkedin && !F(existing, 'LinkedIn')) updates['LinkedIn'] = row.linkedin;
        if (row.influence && !F(existing, 'Level of Influence')) updates['Level of Influence'] = row.influence;
        if (row.source && !F(existing, 'Source')) updates['Source'] = row.source;
        if (row.country && !F(existing, 'Country')) updates['Country'] = row.country;

        // Account field — if stakeholder has no account and CSV provides one, link it (creating the account if needed)
        const existingAccIds = linkedIds(existing, 'Account');
        let linkedAcc = existingAccIds.length > 0 ? accounts.find(ac => ac.id === existingAccIds[0]) || accountMap.get((row.accountName || '').toLowerCase().trim()) : null;
        if (existingAccIds.length === 0 && row.accountName) {
          const resolvedAcc = await resolveOrCreateAccount(row);
          if (resolvedAcc?.id) {
            updates['Account'] = [resolvedAcc.id];
            linkedAcc = resolvedAcc;
          }
        }

        if (Object.keys(updates).length > 0) {
          await a.updateRecord(TABLE_IDS.stakeholders, existing.id, updates);
          updated++;
        } else {
          updateSkipped++;
        }

        // Also update the linked account's Website/Industry if empty (same logic as creation)
        if (linkedAcc) {
          const accUpdate = {};
          if (row.website && !accountWebsiteUpdated.has(linkedAcc.id) && !F(linkedAcc, 'Website')) {
            accUpdate['Website'] = row.website;
            accountWebsiteUpdated.add(linkedAcc.id);
            websitesAdded++;
          }
          if (row.industry && !accountIndustryUpdated.has(linkedAcc.id) && !F(linkedAcc, 'Industry')) {
            accUpdate['Industry'] = row.industry;
            accountIndustryUpdated.add(linkedAcc.id);
            industriesAdded++;
          }
          if (Object.keys(accUpdate).length > 0) {
            try { await a.updateRecord(TABLE_IDS.accounts, linkedAcc.id, accUpdate); } catch {}
          }
        }
        await new Promise(r => setTimeout(r, 250));
      } catch (e) { failed++; console.error('[Update duplicate]', e); }
    }

    // Add all newly-created contacts to the campaign's Assigned Stakeholders
    if (createdStakeholderIds.length > 0 && csvCampaignId) {
      try {
        const campaignRec = campaigns.find(c => c.id === csvCampaignId);
        const currentAssigned = campaignRec ? linkedIds(campaignRec, 'Assigned Stakeholders') : [];
        const newAssigned = [...new Set([...currentAssigned, ...createdStakeholderIds])];
        await a.updateRecord(TABLE_IDS.campaigns, csvCampaignId, { 'Assigned Stakeholders': newAssigned });
      } catch (e) { console.warn('[Import] campaign assignment failed:', e); }
    }

    setContactImportResult({ created, failed, updated, updateSkipped, websitesAdded, industriesAdded, accountsCreated });
    setContactImporting(false);
    if (onLogActivity) onLogActivity();
  };

  // The same as in the unified client file (src/contactMessage.js)
  const useMessage = (stakeholder, channel, message, ccList = [], eventId = null) => sendContactMessage({ stakeholder, channel, message, eventId, api, data, onAddRecord, onLogActivity });

  const logMeeting = async (s, notes, date) => {
    const sn = `${F(s,'Name')||''} ${F(s,'Last name')||''}`.trim();
    const companyIds = linkedIds(s, 'Account');
    try {
      const a = api || new AirtableAPI();
      await a.createRecord(TABLE_IDS.outreach, {
        'Activity Name': `Meeting Scheduled: ${sn} — ${new Date().toLocaleDateString('en-US')}`,
        'Account': companyIds, 'Stakeholder': [s.id],
        'Channel': 'Call', 'Date': new Date().toISOString(),
        'Status': 'Meeting Scheduled', 'Message': notes || '',
        'Notes': `Meeting ${date ? `on ${date}` : 'TBD'} — logged from Contacts`,
        'Logged By': CURRENT_USER?.name || '',
      });
      await activateAccountIfNeeded(a, companyIds, data.accounts);
      await updateStakeholderStatus(a, s.id, 'Meeting Booked', data.stakeholders);
      if (onLogActivity) onLogActivity();
    } catch (e) { window.__oikeToast('No se pudo registrar la reunión: ' + e.message, 'error'); }
  };

  const deleteContact = async (s) => {
    const name = `${F(s, 'Name') || ''} ${F(s, 'Last name') || ''}`.trim() || 'this contact';
    if (!confirm(`¿Borrar a ${name}?\n\nSe borra el contacto y su vínculo con la empresa. El historial de interacciones queda guardado, pero ya no va a mostrar su nombre.`)) return;
    try {
      const a = api || new AirtableAPI();
      await a.deleteRecord(TABLE_IDS.stakeholders, s.id);
      if (onDeleteRecord) onDeleteRecord('stakeholders', s.id);
      if (onLogActivity) onLogActivity();
    } catch (e) {
      console.error('[deleteContact] failed:', e);
      window.__oikeToast('No se pudo borrar: ' + (e.message || 'error desconocido'), 'error');
    }
  };

  const saveContactEdit = async (values) => {
    if (!editingContact) return;
    // Only send fields that have a value — Airtable rejects empty strings for Email, Phone, URL, and Single Select fields
    const raw = {
      'Name': values['Name'],
      'Last name': values['Last name'],
      'Role': values['Role'],
      'Email': values['Email'],
      'Phone number': values['Phone number'],
      'LinkedIn': values['LinkedIn'],
      'Campaign': values['Campaign'] ? [values['Campaign']] : null, // linked record by ID
      'Country': values['Country'] || null,
      'Level of Influence': values['Level of Influence'] || null,
      'Source': values['Source'] || null,
    };
    const updatedFields = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== '' && v !== undefined && v !== null));
    if (onUpdateRecord) onUpdateRecord('stakeholders', editingContact.id, updatedFields);
    setEditingContact(null);
    const a = api || new AirtableAPI();
    a.updateRecord(TABLE_IDS.stakeholders, editingContact.id, updatedFields)
      .then(() => { if (onLogActivity) onLogActivity(); })
      .catch(e => { console.error(e); window.__oikeToast('No se pudo guardar el contacto: ' + (e.message || 'error desconocido'), 'error'); if (onLogActivity) onLogActivity(); });
  };

  const influenceLevels = useMemo(() => {
    const levels = new Set();
    stakeholders.forEach(s => { const l = F(s, 'Level of Influence'); if (l) levels.add(l); });
    return [...levels].sort();
  }, [stakeholders]);

  // Build map stakeholderId → deal role from Strategy JSON on each account
  const stakeholderRoleMap = useMemo(() => {
    const map = {};
    (accounts || []).forEach(a => {
      try {
        const raw = F(a, 'Strategy');
        if (!raw) return;
        const strategy = JSON.parse(raw);
        const roles = strategy.stakeholderRoles || {};
        Object.entries(roles).forEach(([stkId, role]) => { if (role && role !== 'Unknown') map[stkId] = role; });
      } catch {}
    });
    return map;
  }, [accounts]);

  const filtered = useMemo(() => stakeholders.filter(s => {
    if (searchName && !(F(s, 'Name') || '').toLowerCase().includes(searchName.toLowerCase())) return false;
    if (searchAccount) {
      const term = searchAccount.toLowerCase();
      const accNames = resolveLinked(s, 'Account', accounts, 'Account Name');
      if (!accNames.some(n => n.toLowerCase().includes(term))) return false;
    }
    if (selectedInfluence && F(s, 'Level of Influence') !== selectedInfluence) return false;
    if (filterSource && F(s, 'Source') !== filterSource) return false;
    if (filterCountry && (F(s, 'Country') || '').toLowerCase() !== filterCountry.toLowerCase()) return false;
    if (filterDealRole && stakeholderRoleMap[s.id] !== filterDealRole) return false;
    if (filterStatus) {
      const sStatus = F(s, 'Status') || 'Not Contacted';
      if (sStatus !== filterStatus) return false;
    }
    return true;
  }).sort((a, b) => (F(a, 'Name') || '').localeCompare(F(b, 'Name') || '')), [stakeholders, searchName, searchAccount, selectedInfluence, filterSource, filterStatus, filterCountry, filterDealRole, stakeholderRoleMap, accounts]);

  const bulkGeneratePains = async (targets) => {
    if (bulkPainProgress) return;
    const pending = targets.filter(s => !(F(s,'Pain points') || F(s,'Pain Points (Generated)') || '').trim());
    if (pending.length === 0) { window.__oikeToast('Todos los contactos filtrados ya tienen dolores generados.', 'warning'); return; }
    setBulkPainProgress({ done: 0, total: pending.length });
    const a = api || new AirtableAPI();
    for (let i = 0; i < pending.length; i++) {
      const s = pending[i];
      const sName = `${F(s,'Name')||''} ${F(s,'Last name')||''}`.trim();
      const role = F(s,'Role') || '';
      const accId = linkedIds(s,'Account')[0];
      const acc = accId ? accounts.find(ac => ac.id === accId) : null;
      const accName = acc ? F(acc,'Account Name') : '';
      const industry = acc ? F(acc,'Industry') : '';
      const news = acc ? (F(acc,'Recent News')||'').slice(0,300) : '';
      try {
        const prompt = `B2B sales analyst. Generate 3-5 specific pain points for this stakeholder.
PERSON: ${sName} | ${role}
COMPANY: ${accName}${industry ? ` | ${industry}` : ''}
${news ? `COMPANY NEWS: ${news}` : ''}
SELLER: ${COMPANY_PROFILE.companyName} — ${COMPANY_PROFILE.services}

Pain points must be specific to their role, reference industry challenges, and connect to where ${COMPANY_PROFILE.companyName} can help. Bullet points, 1-2 sentences each. Write ONLY the pain points.`;
        const result = await callOpenAI({ prompt, temperature: 0.7, max_tokens: 350 });
        await a.updateRecord(TABLE_IDS.stakeholders, s.id, { 'Pain points': result });
        if (onUpdateRecord) onUpdateRecord('stakeholders', s.id, { 'Pain points': result });
      } catch(e) {
        console.error('Pain gen failed for', sName, e);
        window.__oikeToast?.(`Error generando pains para ${sName}: ${e?.message || e}`, 'error');
        setBulkPainProgress(null);
        return;
      }
      setBulkPainProgress({ done: i + 1, total: pending.length });
      if (i < pending.length - 1) await new Promise(r => setTimeout(r, 800));
    }
    setBulkPainProgress(null);
    window.__oikeToast?.(`✅ ${pending.length} pains generados`, 'success');
    if (onLogActivity) onLogActivity();
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Contactos</h1>
          <p>Todos los contactos · {stakeholders.length} en total</p>
          <p style={{ fontSize: 12, color: 'var(--globant-muted)', margin: '2px 0 0', maxWidth: 720 }}>Un contacto es el registro comercial de una persona: su empresa, su historial, sus solicitudes, cotizaciones y pagos.{CURRENT_USER?.role === 'admin' ? ' Quien participa en tus servicios tiene además su ficha en Clientes › Personas, vinculada por id; nada se une solo por nombre o email.' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <button className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setShowContactImport(!showContactImport); setContactImportResult(null); }}>
            {showContactImport ? 'Cerrar importación' : 'Importar CSV'}
          </button>
          <button className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={() => bulkGeneratePains(filtered)} disabled={!!bulkPainProgress}>
            {bulkPainProgress ? `Generando ${bulkPainProgress.done}/${bulkPainProgress.total}…` : 'Generar dolores con IA'}
          </button>
          <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={() => setShowNewContact(!showNewContact)}>
            {showNewContact ? 'Cerrar' : 'Nuevo contacto'}
          </button>
        </div>
      </div>

      <div className="filters-row">
        <input className="input-field" style={{ maxWidth: 220 }} placeholder="Buscar por nombre…" value={searchName} onChange={e => setSearchName(e.target.value)} />
        <input className="input-field" style={{ maxWidth: 220 }} placeholder="Filtrar por empresa…" value={searchAccount} onChange={e => setSearchAccount(e.target.value)} />
        <select className="input-field" style={{ maxWidth: 200 }} value={filterDealRole} onChange={e => setFilterDealRole(e.target.value)}>
          <option value="">Todos los roles</option>
          <option value="Champion">Promotor</option>
          <option value="Blocker">Bloquea</option>
          <option value="Economic Buyer">Decide la compra</option>
          <option value="Evaluator">Evalúa</option>
        </select>
        <select className="input-field" style={{ maxWidth: 200 }} value={filterSource} onChange={e => setFilterSource(e.target.value)}>
          <option value="">Todos los orígenes</option>
          {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{es(s)}</option>)}
        </select>
        <select className="input-field" style={{ maxWidth: 200 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          {STAKEHOLDER_STATUS_OPTIONS.map(s => <option key={s} value={s}>{es(s)}</option>)}
        </select>
        <select className="input-field" style={{ maxWidth: 180 }} value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
          <option value="">Todos los países</option>
          {[...new Set(stakeholders.map(s => F(s,'Country')).filter(Boolean))].sort().map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* New Contact Form: the essentials first, the commercial details folded away */}
      {showNewContact && (() => {
        const emailInvalid = ctxNewEmail.trim() !== '' && !EMAIL_RE.test(ctxNewEmail.trim());
        return (
        <div className="card contact-form" style={{ borderLeft: '3px solid var(--globant-green)', marginBottom: 16 }}>
          <div className="card-header"><h3>Nuevo contacto</h3></div>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="nc-name">Nombre *</label>
              <input id="nc-name" className="input-field" placeholder="Ej.: Camila" value={ctxNewName} onChange={e => setCtxNewName(e.target.value)} autoFocus />
            </div>
            <div className="form-field">
              <label htmlFor="nc-last">Apellido</label>
              <input id="nc-last" className="input-field" placeholder="Ej.: Ortiz" value={ctxNewLast} onChange={e => setCtxNewLast(e.target.value)} />
            </div>
            <div className="form-field">
              <label htmlFor="nc-email">Email<InfoTip text="El email de trabajo. Se usa para escribirle y para evitar contactos repetidos." /></label>
              <input id="nc-email" className="input-field" type="email" placeholder="nombre@empresa.com" value={ctxNewEmail} onChange={e => setCtxNewEmail(e.target.value)} aria-invalid={emailInvalid} aria-describedby={emailInvalid ? 'nc-email-error' : undefined} />
              {emailInvalid && <div id="nc-email-error" className="form-error">Revisá el email: parece incompleto.</div>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-field form-field-wide">
              <label htmlFor="nc-account">Empresa <span className="form-optional">(opcional)</span><InfoTip text="La empresa donde trabaja. Podés elegir una que ya existe o crear una nueva. Si es independiente, dejalo vacío." /></label>
              {!showNewAccount ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <select id="nc-account" className="input-field" style={{ flex: 1 }} value={ctxNewAccountId} onChange={e => setCtxNewAccountId(e.target.value)}>
                    <option value="">Sin empresa</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{F(a, 'Account Name')}</option>)}
                  </select>
                  {CURRENT_USER?.role === 'admin' && <button type="button" className="action-btn btn-ghost" style={{ whiteSpace: 'nowrap' }} onClick={() => setShowNewAccount(true)}>+ Nueva empresa</button>}
                </div>
              ) : (
                <div className="form-inline-box">
                  <input className="input-field" aria-label="Nombre de la empresa" placeholder="Nombre de la empresa *" value={ctxNewAccountName} onChange={e => setCtxNewAccountName(e.target.value)} />
                  <input className="input-field" aria-label="Web de la empresa" placeholder="Web (opcional)" value={ctxNewAccountWebsite} onChange={e => setCtxNewAccountWebsite(e.target.value)} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="action-btn btn-primary" onClick={createInlineAccount} disabled={!ctxNewAccountName.trim() || ctxCreatingAccount}>{ctxCreatingAccount ? 'Creando…' : 'Crear empresa'}</button>
                    <button type="button" className="action-btn btn-ghost" onClick={() => { setShowNewAccount(false); setCtxNewAccountName(''); setCtxNewAccountWebsite(''); }}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <details className="form-more">
            <summary>Más datos comerciales <span className="form-optional">(cargo, teléfono, LinkedIn, influencia, origen, campaña)</span></summary>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="nc-role">Cargo<InfoTip text="Su puesto, por ejemplo 'Gerente comercial'. Se usa en los mensajes con IA." /></label>
                <input id="nc-role" className="input-field" placeholder="Ej.: Gerente comercial" value={ctxNewRole} onChange={e => setCtxNewRole(e.target.value)} />
              </div>
              <div className="form-field">
                <label htmlFor="nc-phone">Teléfono<InfoTip text="Celular o teléfono de oficina, para WhatsApp o llamadas." /></label>
                <input id="nc-phone" className="input-field" type="tel" placeholder="+56 9…" value={ctxNewPhone} onChange={e => setCtxNewPhone(e.target.value)} />
              </div>
              <div className="form-field">
                <label htmlFor="nc-linkedin">LinkedIn<InfoTip text="El link completo a su perfil de LinkedIn." /></label>
                <input id="nc-linkedin" className="input-field" placeholder="https://linkedin.com/in/…" value={ctxNewLinkedin} onChange={e => setCtxNewLinkedin(e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="nc-influence">Influencia<InfoTip text="Cuánto pesa su opinión en la decisión de compra. Decide: tiene la última palabra. Promotor: la impulsa por dentro. Influye: forma opinión." /></label>
                <select id="nc-influence" className="input-field" value={ctxNewInfluence} onChange={e => setCtxNewInfluence(e.target.value)}>
                  <option value="">Sin definir</option>
                  <option value="Decision Maker">Decide</option>
                  <option value="High">Alta</option>
                  <option value="Influencer">Influye</option>
                  <option value="Champion">Promotor</option>
                  <option value="Medium">Media</option>
                  <option value="Low">Baja</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="nc-source">Origen<InfoTip text="Cómo llegaste a este contacto. Sirve para ver qué canales traen más oportunidades." /></label>
                <select id="nc-source" className="input-field" value={ctxNewSource} onChange={e => setCtxNewSource(e.target.value)}>
                  <option value="">Sin definir</option>
                  {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{es(s)}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="nc-campaign">Campaña<InfoTip text="Vinculá el contacto con una campaña que ya existe." /></label>
                <select id="nc-campaign" className="input-field" value={ctxNewCampaign} onChange={e => setCtxNewCampaign(e.target.value)}>
                  <option value="">Sin campaña</option>
                  {campaigns.sort((a,b) => (F(a,'Name')||'').localeCompare(F(b,'Name')||'')).map(c => (
                    <option key={c.id} value={c.id}>{F(c,'Name')}{F(c,'Status') ? ` (${es(F(c,'Status'))})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          </details>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="action-btn btn-primary" onClick={createContact} disabled={!ctxNewName.trim() || emailInvalid || ctxCreating}>
              {ctxCreating ? 'Creando…' : 'Crear contacto'}
            </button>
            <button type="button" className="action-btn btn-ghost" onClick={() => setShowNewContact(false)}>Cancelar</button>
          </div>
        </div>
        );
      })()}

      {/* CSV Import Card */}
      {showContactImport && (
        <div className="card" style={{ borderLeft: '3px solid #7c3aed', marginBottom: 16 }}>
          <div className="card-header">
            <h3>Importar contactos desde CSV</h3>
            <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Columnas que se reconocen: First Name, Last Name, Email, Phone, Role, LinkedIn, Account, Website, Industry, Country, Source, Campaign</span>
          </div>
          {!contactCsvRows.length ? (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
                  Assign to Campaign <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional — applies to all imported contacts)</span>
                </label>
                <select className="input-field" style={{ fontSize: 12, padding: '6px 8px', minWidth: 260 }} value={csvCampaignId} onChange={e => setCsvCampaignId(e.target.value)}>
                  <option value="">Sin campaña</option>
                  {campaigns.sort((a,b) => (F(a,'Name')||'').localeCompare(F(b,'Name')||'')).map(c => (
                    <option key={c.id} value={c.id}>{F(c,'Name')}{F(c,'Status') ? ` (${F(c,'Status')})` : ''}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <input type="file" accept=".csv" onChange={handleContactCsv} onClick={e => e.target.value = ''} style={{ fontSize: 12, color: 'var(--globant-muted)', flex: 1 }} />
                <button className="action-btn btn-ghost" style={{ fontSize: 11, whiteSpace: 'nowrap' }} onClick={() => {
                  const csv = 'First Name,Last Name,Email,Phone number,Role,LinkedIn,Level of Influence,Account,Website,Industry,Country,Source,Campaign\nAna,García,ana@empresa.com,+5491112345678,CEO,https://linkedin.com/in/ana,High,Empresa SRL,https://empresa.com,SaaS / Software,Argentina,LinkedIn,\nJohn,Smith,john@acme.io,,Head of Sales,,Medium,Acme Inc,https://acme.io,Fintech,United States,,';
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = 'contacts_template.csv'; a.click();
                }}>Descargar plantilla</button>
              </div>
              <p style={{ fontSize: 11, color: 'var(--globant-muted)', marginTop: 4 }}>
                Tip: Column headers must match exactly (case-insensitive). Country is auto-inherited from the account if not specified. Duplicates by email or full name are auto-detected. You can also include a "Campaign" column in the CSV to assign per-row.
              </p>
            </div>
          ) : (
            <div>
              {contactImportResult ? (
                <div style={{ padding: '12px', background: 'rgba(91,191,181,0.08)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                  ✅ Import complete — <strong>{contactImportResult.created}</strong> created{contactImportResult.updated > 0 ? `, 🔄 ${contactImportResult.updated} updated` : ''}{contactImportResult.updateSkipped > 0 ? ` (${contactImportResult.updateSkipped} already complete)` : ''}{contactImportResult.failed > 0 ? `, ❌ ${contactImportResult.failed} failed` : ''}{contactImportResult.accountsCreated > 0 ? ` · 🏢 ${contactImportResult.accountsCreated} new account${contactImportResult.accountsCreated !== 1 ? 's' : ''}` : ''}{contactImportResult.websitesAdded > 0 ? ` · 🌐 ${contactImportResult.websitesAdded} website${contactImportResult.websitesAdded !== 1 ? 's' : ''}` : ''}{contactImportResult.industriesAdded > 0 ? ` · 🏭 ${contactImportResult.industriesAdded} industr${contactImportResult.industriesAdded !== 1 ? 'ies' : 'y'}` : ''}.
                  <button className="action-btn btn-ghost" style={{ fontSize: 11, marginLeft: 12 }} onClick={() => { setContactCsvRows([]); setContactImportResult(null); }}>Importar otro</button>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--globant-muted)' }}>
                    {contactCsvRows.length} rows parsed · <span style={{ color: '#B4362C' }}>{contactCsvRows.filter(r => r.isDuplicate).length} exact duplicates</span>{contactCsvRows.filter(r => r.isFuzzy).length > 0 && <> · <span style={{ color: '#9A6212' }}>{contactCsvRows.filter(r => r.isFuzzy).length} possible duplicates</span></>} · <span style={{ color: 'var(--globant-green)' }}>{contactCsvRows.filter(r => r.selected && !r.isDuplicate).length} to import</span>
                  </div>
                  <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                    <table className="data-table" style={{ fontSize: 11 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}>
                            <input type="checkbox" checked={contactCsvRows.every(r => r.isDuplicate || r.selected)} onChange={e => setContactCsvRows(rows => rows.map(r => r.isDuplicate ? r : { ...r, selected: e.target.checked }))} />
                          </th>
                          <th>Nombre</th><th>Empresa</th><th>Email</th><th>País</th><th>Origen</th><th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactCsvRows.map((row, i) => (
                          <tr key={i} style={{ opacity: row.isDuplicate ? 0.45 : 1, background: row.isFuzzy ? 'rgba(251,191,36,0.04)' : 'transparent' }}>
                            <td><input type="checkbox" checked={row.selected && !row.isDuplicate} disabled={row.isDuplicate} onChange={e => setContactCsvRows(rows => rows.map((r, j) => j === i ? { ...r, selected: e.target.checked } : r))} /></td>
                            <td>{row.firstName} {row.lastName}</td>
                            <td>{row.accountName || '—'}</td>
                            <td>{row.email || '—'}</td>
                            <td style={{ fontSize: 11 }}>{row.country ? <span style={{ color: 'var(--globant-muted)' }}>{row.country}</span> : <span style={{ color: 'rgba(30,42,44,0.2)' }}>—</span>}</td>
                            <td>{row.source || '—'}</td>
                            <td>
                              {row.isDuplicate
                                ? <span className="badge" style={{ background: 'rgba(239,68,68,0.15)', color: '#B4362C', fontSize: 9 }}>🚫 {row.duplicateReason}</span>
                                : row.isFuzzy
                                ? <span className="badge" style={{ background: 'rgba(251,191,36,0.15)', color: '#9A6212', fontSize: 9 }} title={row.fuzzyReason}>⚠️ {row.fuzzyReason}</span>
                                : <span className="badge badge-green" style={{ fontSize: 9 }}>Nuevo</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: 'var(--globant-text)' }}>
                      <input type="checkbox" checked={updateDuplicates} onChange={e => setUpdateDuplicates(e.target.checked)} style={{ cursor: 'pointer' }} />
                      <span>Actualizar duplicados con los datos nuevos <span style={{ color: 'var(--globant-muted)', fontSize: 11 }}>(fills empty fields only, never overwrites)</span></span>
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {(() => {
                      const toCreate = contactCsvRows.filter(r => r.selected && !r.isDuplicate).length;
                      const toUpdate = updateDuplicates ? contactCsvRows.filter(r => r.isDuplicate).length : 0;
                      const total = toCreate + toUpdate;
                      const label = toUpdate > 0
                        ? `Importar (${toCreate} nuevos${toUpdate > 0 ? ` + ${toUpdate} actualizaciones` : ''})`
                        : `Importar ${toCreate} contactos`;
                      return (
                        <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={importContacts} disabled={contactImporting || total === 0}>
                          {contactImporting ? 'Importando…' : label}
                        </button>
                      );
                    })()}
                    <button className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setContactCsvRows([]); setContactImportResult(null); }}>Volver a subir</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Contacts Table */}
      <div className="card">
        <div className="card-header">
          <h3>Todos los contactos ({filtered.length})</h3>
          {(searchName || searchAccount || filterDealRole) && <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Filtrados de {stakeholders.length} en total</span>}
        </div>
        {filtered.length === 0 ? (
          <p style={{ color: 'var(--globant-muted)', fontSize: 13, padding: '12px 0' }}>No hay contactos. {stakeholders.length === 0 ? 'Agrega el primero con Nuevo contacto.' : 'Prueba quitando filtros.'}</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Contacto</th>
                <th>Empresa</th>
                <th>Estado</th>
                <th>Influencia</th>
                <th>Origen</th>
                <th style={{ textAlign: 'center' }}>Contactos hechos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const accNames = resolveLinked(s, 'Account', accounts, 'Account Name');
                const phone = F(s, 'Phone number');
                const email = F(s, 'Email');
                const linkedin = F(s, 'LinkedIn');
                const touches = outreach.filter(o => linkedIds(o, 'Stakeholder').includes(s.id)).length;
                const fallback = `Hi ${F(s, 'Name')}, reaching out from ${COMPANY_PROFILE.companyName} regarding potential collaboration.`;
                return (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--globant-green)' }} onClick={() => { setReturn('Gestión de contactos', { page: 'contacts' }, ['client']); window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'client', contactId: s.id } })); }}>
                        {F(s, 'Name')}{F(s, 'Last name') ? ` ${F(s, 'Last name')}` : ''}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>{F(s, 'Role')}</div>
                    </td>
                    <td style={{ fontSize: 12 }}>{accNames.join(', ')}</td>
                    <td>{(() => {
                      const status = F(s, 'Status') || 'Not Contacted';
                      const colors = {
                        'Not Contacted': { bg: 'rgba(30,42,44,0.06)', fg: '#56625F' },
                        'Contacted':     { bg: 'rgba(251,191,36,0.15)', fg: '#9A6212' },
                        'Replied':       { bg: 'rgba(74,222,128,0.15)', fg: '#1E7B4F' },
                        'Meeting Booked':{ bg: 'rgba(96,165,250,0.15)', fg: '#2D5F99' },
                        'Not Interested':{ bg: 'rgba(249,115,22,0.15)', fg: '#A0521A' },
                        'DNC':           { bg: 'rgba(239,68,68,0.20)',  fg: '#B4362C' },
                        'Bounced':       { bg: 'rgba(234,88,12,0.15)',  fg: '#A0521A' },
                        'Left Company':  { bg: 'rgba(156,163,175,0.15)',fg: '#56625F' },
                        'Nurture':       { bg: 'rgba(167,139,250,0.15)',fg: '#6B5CA5' },
                      }[status] || { bg: 'rgba(30,42,44,0.06)', fg: '#56625F' };
                      return <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: colors.bg, color: colors.fg, fontWeight: 700, whiteSpace: 'nowrap' }}>{es(status)}</span>;
                    })()}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {F(s, 'Level of Influence') && <span className="badge badge-accent">{es(F(s, 'Level of Influence'))}</span>}
                        {stakeholderRoleMap[s.id] && (() => {
                          const roleColors = { 'Champion': '#1E7B4F', 'Blocker': '#B4362C', 'Economic Buyer': '#9A6212', 'Evaluator': '#2D5F99' };
                          const roleIcons = { 'Champion': '🏆', 'Blocker': '🚫', 'Economic Buyer': '💰', 'Evaluator': '🔍' };
                          const role = stakeholderRoleMap[s.id];
                          const color = roleColors[role] || '#94a3b8';
                          return <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: `${color}20`, color, fontWeight: 700, whiteSpace: 'nowrap' }}>{es(role)}</span>;
                        })()}
                        {!F(s, 'Level of Influence') && !stakeholderRoleMap[s.id] && <span style={{ color: 'var(--globant-muted)' }}>—</span>}
                      </div>
                    </td>
                    <td>{(() => {
                      const rawSrc = s.fields?.['Source'];
                      const srcIds = Array.isArray(rawSrc) ? rawSrc : (rawSrc && String(rawSrc).startsWith('rec') ? [rawSrc] : []);
                      const srcRec = srcIds.length ? sources.find(r => r.id === srcIds[0]) : null;
                      const srcName = srcRec ? (F(srcRec,'Source Name') || F(srcRec,'Source') || srcIds[0]) : (typeof rawSrc === 'string' && rawSrc && !rawSrc.startsWith('rec') ? rawSrc : null);
                      const rawCamp = s.fields?.['Campaign'];
                      const campIds = Array.isArray(rawCamp) ? rawCamp : (rawCamp && String(rawCamp).startsWith('rec') ? [rawCamp] : []);
                      const campRec = campIds.length ? campaigns.find(c => c.id === campIds[0]) : null;
                      const campName = campRec ? F(campRec,'Name') : (typeof rawCamp === 'string' && rawCamp && !rawCamp.startsWith('rec') ? rawCamp : null);
                      return (<>
                        {srcName ? (
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: isInbound(srcName) ? 'rgba(124,58,237,0.15)' : 'rgba(91,191,181,0.15)', color: isInbound(srcName) ? '#6B5CA5' : 'var(--globant-green)', whiteSpace: 'nowrap' }}>{srcName}</span>
                        ) : <span style={{ color: 'var(--globant-muted)', fontSize: 11 }}>—</span>}
                        {campName && <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginTop: 2 }}>{campName}</div>}
                      </>);
                    })()}</td>
                    <td style={{ textAlign: 'center', fontSize: 13 }}>
                      {touches > 0 ? <span style={{ color: 'var(--globant-green)', fontWeight: 700 }}>{touches}</span> : <span style={{ color: 'var(--globant-muted)' }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {phone && <button className="action-btn btn-whatsapp" style={{ fontSize: 11, padding: '4px 8px' }} title="WhatsApp" onClick={() => useMessage(s, 'WhatsApp', fallback)}>💬</button>}
                        {email && <button className="action-btn btn-email" style={{ fontSize: 11, padding: '4px 8px' }} title="Email" onClick={() => useMessage(s, 'Email', fallback)}>✉️</button>}
                        {linkedin && <button className="action-btn btn-linkedin" style={{ fontSize: 11, padding: '4px 8px' }} title="LinkedIn" onClick={() => useMessage(s, 'LinkedIn', fallback)}>🔗</button>}
                        {phone && <button className="action-btn btn-call" style={{ fontSize: 11, padding: '4px 8px' }} title="Llamar" onClick={() => useMessage(s, 'Call', fallback)}>📞</button>}
                        <button title="Agendar reunión" style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(96,165,250,0.3)', background: 'rgba(96,165,250,0.12)', color: '#2D5F99', cursor: 'pointer' }} onClick={() => { setMeetingModal({ stakeholder: s }); setMeetingDate(''); setMeetingTime(''); setMeetingNotes(''); }}>📅</button>
                        <button title="Editar contacto" style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--globant-border)', background: 'rgba(30,42,44,0.04)', color: 'var(--globant-muted)', cursor: 'pointer' }} onClick={() => setEditingContact(s)}>✏️</button>
                        <button title="Borrar contacto" style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(229,115,115,0.3)', background: 'rgba(229,115,115,0.08)', color: '#B4362C', cursor: 'pointer' }} onClick={() => deleteContact(s)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>


      {historyStakeholder && (
        <StakeholderHistoryModal
          stakeholder={historyStakeholder}
          outreach={outreach}
          accounts={accounts}
          onClose={() => setHistoryStakeholder(null)}
          onRefresh={onLogActivity}
          onAddRecord={onAddRecord}
          allData={data}
          onNavigateToAccount={goToAccount}
          onSend={useMessage}
        />
      )}
      {editingContact && (() => {
        const currentCampaignId = (editingContact.fields?.['Campaign'] || [])[0] || '';
        const campaignOptions = [{ value: '', label: 'Sin campaña' },
          ...campaigns.sort((a,b) => (F(a,'Name')||'').localeCompare(F(b,'Name')||'')).map(c => ({ value: c.id, label: `${F(c,'Name')}${F(c,'Status') ? ` (${F(c,'Status')})` : ''}` }))
        ];
        return (
          <EditModal
            title={`${F(editingContact, 'Name')} ${F(editingContact, 'Last name') || ''}`.trim()}
            fields={[
              { key: 'Name', label: 'Nombre' },
              { key: 'Last name', label: 'Apellido' },
              { key: 'Role', label: 'Cargo' },
              { key: 'Email', label: 'Email' },
              { key: 'Phone number', label: 'Teléfono' },
              { key: 'LinkedIn', label: 'LinkedIn' },
              { key: 'Level of Influence', label: 'Influence', type: 'select', options: ['Decision Maker', 'High', 'Influencer', 'Champion', 'Medium', 'Low'] },
              { key: 'Country', label: 'Country' },
              { key: 'Source', label: 'Source', type: 'select', options: SOURCE_OPTIONS },
              { key: 'Campaign', label: 'Campaña', type: 'select', options: campaignOptions },
            ]}
            initialValues={{ ...(editingContact.fields || {}), Campaign: currentCampaignId }}
            onSave={saveContactEdit}
            onClose={() => setEditingContact(null)}
          />
        );
      })()}

      {meetingModal && (() => {
        const ms = meetingModal.stakeholder;
        const msName = `${F(ms,'Name')||''} ${F(ms,'Last name')||''}`.trim();
        const msRole = F(ms,'Role') || '';
        const msEmail = F(ms,'Email') || '';
        const accIds = linkedIds(ms,'Account');
        const msAcc = accounts.find(a => accIds.includes(a.id));
        const msAccName = msAcc ? (F(msAcc,'Account Name')||'') : '';
        const buildCalUrl = () => {
          const start = localInstant(meetingDate, meetingTime || '09:00');
          const title = `${COMPANY_PROFILE.companyName} x ${msAccName || 'Account'} — ${msName}`;
          const details = `Meeting with ${msName}${msRole ? ` (${msRole})` : ''}${msAccName ? ` at ${msAccName}` : ''}\n\n${meetingNotes || 'Intro call'}`;
          return googleCalendarUrl({ title, start, minutes: 60, details, guests: [msEmail] });
        };
        const calUrl = buildCalUrl();
        return (
          <div className="modal-overlay" onClick={() => setMeetingModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h3>Agendar reunión</h3>
              <div style={{ fontSize: 13, color: 'var(--globant-muted)', marginBottom: 4 }}>{msName}{msRole ? ` · ${msRole}` : ''}</div>
              {msAccName && <div style={{ fontSize: 12, color: 'var(--globant-accent)', marginBottom: 14 }}>{msAccName}</div>}
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--globant-muted)', marginBottom: 4, fontWeight: 600 }}>FECHA</label>
                  <input type="date" className="input-field" style={{ width: '100%' }} value={meetingDate} onChange={e => setMeetingDate(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--globant-muted)', marginBottom: 4, fontWeight: 600 }}>HORA</label>
                  <input type="time" className="input-field" style={{ width: '100%' }} value={meetingTime} onChange={e => setMeetingTime(e.target.value)} />
                </div>
              </div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--globant-muted)', marginBottom: 4, fontWeight: 600 }}>NOTAS / TEMARIO</label>
              <textarea className="input-field" style={{ width: '100%', minHeight: 70, resize: 'vertical', marginBottom: 14, fontFamily: 'inherit', fontSize: 12 }}
                placeholder="Tema y temario…" value={meetingNotes} onChange={e => setMeetingNotes(e.target.value)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="action-btn btn-ghost" style={{ flex: 1 }} onClick={() => setMeetingModal(null)}>Cancelar</button>
                {calUrl && (
                  <button className="action-btn" style={{ flex: 1, background: 'rgba(66,133,244,0.15)', color: '#2D5F99', border: '1px solid rgba(66,133,244,0.3)' }}
                    onClick={() => window.open(calUrl, '_blank')}>Abrir en el calendario</button>
                )}
                <button className="action-btn" style={{ flex: 1, background: 'rgba(96,165,250,0.2)', color: '#2D5F99', border: '1px solid rgba(96,165,250,0.4)' }}
                  onClick={async () => {
                    if (calUrl) window.open(calUrl, '_blank');
                    await logMeeting(ms, meetingNotes, meetingDate);
                    setMeetingModal(null);
                  }}>
                  ✅ Log + Add to Calendar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ============ ACTIVITY TRACKER ============

export default ContactsSection;
